import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { resolveUrl } from "@/lib/api";
import { CodeBlock } from "@/components/CodeBlock";
import { Reveal } from "@/components/Reveal";
import { ProjectDetailSkeleton } from "@/components/Skeletons";
import { Seo, SITE_URL } from "@/components/Seo";
import { ArrowLeft, ExternalLink, Github } from "lucide-react";

function Section({ id, title, children }) {
  if (!children) return null;
  return (
    <Reveal>
      <section id={id} className="scroll-mt-28" data-testid={`case-section-${id}`}>
        <h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">{title}</h2>
        <div className="text-base sm:text-lg text-muted-foreground leading-relaxed whitespace-pre-line">{children}</div>
      </section>
    </Reveal>
  );
}

export default function ProjectDetail() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/projects/${slug}`).then((r) => setProject(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound)
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="project-not-found">
        <h1 className="text-3xl font-heading">Project not found</h1>
        <Link to="/projects" className="mt-6 inline-flex items-center gap-2 text-muted-foreground hover:text-foreground"><ArrowLeft className="w-4 h-4" /> Back to projects</Link>
      </div>
    );

  if (!project) return <ProjectDetailSkeleton />;

  return (
    <div data-testid="project-detail-page">
      <Seo
        title={project.title}
        description={project.summary}
        image={resolveUrl(project.cover_image)}
        path={`/projects/${project.slug}`}
        type="article"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "CreativeWork",
            "name": project.title,
            "description": project.summary,
            "image": resolveUrl(project.cover_image),
            "keywords": (project.tech_stack || []).join(", "),
            "dateCreated": project.created_at,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": SITE_URL
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Projects",
                "item": `${SITE_URL}/projects`
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": project.title,
                "item": `${SITE_URL}/projects/${project.slug}`
              }
            ]
          }
        ]}
      />
      {/* Hero */}
      <section className="relative">
        <div className="relative h-[42vh] sm:h-[56vh] max-h-[560px] overflow-hidden">
          <img
            src={resolveUrl(project.cover_image)}
            alt={project.title}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-black/30" />
        </div>
        <div className="max-w-6xl mx-auto px-6">
          <div className="-mt-24 relative">
            <Link to="/projects" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-5"><ArrowLeft className="w-4 h-4" /> All projects</Link>
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-mono">{project.category}</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-light tracking-tighter mt-3 max-w-4xl leading-[1.03]">{project.title}</h1>
            <p className="mt-5 text-lg text-muted-foreground max-w-2xl leading-relaxed">{project.summary}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              {project.demo_url && (
                <a href={project.demo_url} target="_blank" rel="noreferrer" data-testid="case-demo-link" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-medium hover:opacity-90 transition-opacity">
                  Live Demo <ExternalLink className="w-4 h-4" />
                </a>
              )}
              {project.repo_url && (
                <a href={project.repo_url} target="_blank" rel="noreferrer" data-testid="case-repo-link" className="inline-flex items-center gap-2 rounded-full glass px-5 py-2.5 text-sm font-medium hover:-translate-y-0.5 transition-transform">
                  <Github className="w-4 h-4" /> Repository
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <div className="max-w-6xl mx-auto px-6 py-16 grid lg:grid-cols-12 gap-12 min-w-0 w-full">
        <div className="lg:col-span-8 space-y-14 min-w-0">
          <Section id="problem" title="Problem">{project.problem}</Section>
          <Section id="goals" title="Goals & Constraints">{project.goals}</Section>
          <Section id="role" title="My Role">{project.role}</Section>
          <Section id="uiux" title="UI / UX Decisions">{project.uiux}</Section>
          <Section id="implementation" title="Key Implementation">{project.implementation}</Section>

          {project.code_snippets?.length > 0 && (
            <Reveal>
              <div className="space-y-6">
                <h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Code Snippets</h2>
                {project.code_snippets.map((s, i) => (
                  <CodeBlock key={i} snippet={s} />
                ))}
              </div>
            </Reveal>
          )}

          <Section id="architecture" title="Architecture">{project.architecture}</Section>
          {project.architecture_image && (
            <Reveal>
              <img
                src={resolveUrl(project.architecture_image)}
                alt="Architecture"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
                className="w-full rounded-2xl glass p-2"
              />
            </Reveal>
          )}
          <Section id="challenges" title="Challenges & Tradeoffs">{project.challenges}</Section>
          <Section id="outcome" title="Outcome & Learnings">{project.outcome}</Section>

          {project.screenshots?.length > 0 && (
            <Reveal>
              <div className="space-y-4">
                <h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Screenshots</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {project.screenshots.map((s, i) => (
                    <img
                      key={i}
                      src={resolveUrl(s)}
                      alt={`Screenshot ${i + 1}`}
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                      className="w-full rounded-2xl object-cover max-h-[80vh] glass p-1.5"
                    />
                  ))}
                </div>
              </div>
            </Reveal>
          )}
        </div>

        {/* Sticky meta */}
        <aside className="lg:col-span-4 min-w-0">
          <div className="sticky top-28 glass rounded-3xl p-6 space-y-6">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Tech Stack</div>
              <div className="flex flex-wrap gap-2">
                {project.tech_stack?.map((t) => (
                  <span key={t} className="text-xs font-mono px-2.5 py-1 rounded-md bg-secondary text-secondary-foreground">{t}</span>
                ))}
              </div>
            </div>
            {project.role && (
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Role</div>
                <p className="text-sm text-muted-foreground">{project.role}</p>
              </div>
            )}
            <Link to="/book" className="block text-center rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-medium hover:opacity-90 transition-opacity">Discuss a project</Link>
          </div>
        </aside>
      </div>
    </div>
  );
}
